// src/platforms/aws/diagrams/utils/lucidTextMeasurement.js
//
// Measures the actual rendered size of the HTML that goes into a Lucid shape's `text`
// field, by laying it out in a hidden DOM element. The Lucid export renders richer text
// than the on-screen React Flow node, so sizing a shape to anything other than its OWN
// rendered content causes the text to overflow the shape. Measuring the real HTML (at a
// chosen max width) gives an accurate box that the content fits inside.
//
// Synchronous: offsetWidth/offsetHeight force layout and return immediately, so the Lucid
// parser can stay synchronous. Results are cached by content so batch exports are cheap.

// Reusable hidden host element (created lazily, once, on document.body).
let measureEl = null;

// Cache keyed by `${maxWidth}|${style}|${html}` -> { width, height }.
const measurementCache = new Map();

function getMeasureElement() {
  if (measureEl) return measureEl;
  if (typeof document === 'undefined' || !document.body) return null;

  measureEl = document.createElement('div');
  // Off-screen, non-interactive, doesn't affect page layout.
  Object.assign(measureEl.style, {
    position: 'absolute',
    top: '-99999px',
    left: '-99999px',
    visibility: 'hidden',
    pointerEvents: 'none',
    display: 'inline-block',
    boxSizing: 'border-box',
    // Let text wrap up to max-width (set per call) and break long tokens.
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  });
  document.body.appendChild(measureEl);
  return measureEl;
}

/**
 * Measure the rendered size of a Lucid shape's HTML text.
 *
 * @param {string} html        - The inner HTML placed in the shape's <p> (without the <p> wrapper).
 * @param {string} styleString - The inline style applied to the shape's <p> (font, size, weight…).
 * @param {object} [opts]
 * @param {number} [opts.maxWidth=320] - Max content width before wrapping (px).
 * @returns {{width:number, height:number} | null} measured content size, or null if no DOM.
 */
export function measureLucidHtml(html, styleString = '', { maxWidth = 320 } = {}) {
  const safeHtml = html == null ? '' : String(html);
  const cacheKey = `${maxWidth}|${styleString}|${safeHtml}`;
  if (measurementCache.has(cacheKey)) return measurementCache.get(cacheKey);

  const el = getMeasureElement();
  if (!el) return null; // Non-DOM environment — caller falls back to the heuristic.

  // Reset to the off-screen base styling plus the shape's own typography (font-family/
  // size/weight/line-height from styleString) so the measured metrics match Lucid's render.
  el.style.cssText = `position:absolute;top:-99999px;left:-99999px;visibility:hidden;` +
    `pointer-events:none;display:inline-block;box-sizing:border-box;white-space:normal;` +
    `word-wrap:break-word;overflow-wrap:break-word;max-width:${maxWidth}px;${styleString}`;
  el.innerHTML = safeHtml;

  const width = el.offsetWidth;
  const height = el.offsetHeight;
  const result = { width, height };
  measurementCache.set(cacheKey, result);
  return result;
}

/**
 * Clear the measurement cache. Call at batch-export boundaries to bound memory growth.
 */
export function resetLucidMeasurementCache() {
  measurementCache.clear();
}
